<template>
    <!-- Upload Revenue Breakup Commentary Dialog -->
    <lb-dialog 
      v-model="internalValue" 
      heading="Upload Revenue Breakup Commentary" 
      :width="1500"
      :loading="loading"
    >
      <template v-slot:body>
        <div>
          <!-- File Upload Section -->
          <div class="d-flex mb-2 gp-1">
            <lb-file 
              v-model="uploadFile" 
              label="" 
              :drag="false" 
              hidedetails
              @change="handleFileChange" 
              :displayname="false"
              accept=".xlsx,.xls,.xlsm,.xlsb" 
            />
            <v-btn 
              class="secondaryBtn" 
              @click="handleDownloadTemplate"
            >
              <span>Download Template</span>
            </v-btn>
          </div>
  
          <!-- Data Preview Table -->
          <lb-datatable 
            v-if="uploadData.length > 0"
            :headers="computedHeaders" 
            :items="uploadData"
            :enableslot="enabledSlots"
            :loading="loading"
          >
            <!-- Actual Amount Slot -->
            <template v-slot:actual_amount="{ item }">
              <span>{{ formatNumber(item.actual_amount) }}</span>
            </template>
  
            <!-- Forecast Amount Slot -->
            <template v-slot:forecast_amount="{ item }">
              <span>{{ formatNumber(item.forecast_amount) }}</span>
            </template>
  
            <!-- Proforma Amount Slot -->
            <template v-slot:proforma_amount="{ item }">
              <span>{{ formatNumber(item.proforma_amount) }}</span>
            </template>
  
            <!-- Actual vs Proforma Variance Slot -->
            <template v-slot:actual_vs_proforma_variance_amount="{ item }">
              <span>{{ formatNumber(item.actual_vs_proforma_variance_amount) }}</span>
            </template>
  
            <!-- Actual vs Forecast Variance Slot -->
            <template v-slot:actual_vs_forecast_variance_amount="{ item }">
              <span>{{ formatNumber(item.actual_vs_forecast_variance_amount) }}</span>
            </template>
  
            <!-- Message/Error Slot -->
            <template v-slot:message="{ item }">
              <span 
                :class="item.error ? 'error--text' : 'success--text'"
              >
                {{ item.error || 'Ok' }}
              </span>
            </template>
          </lb-datatable>
  
          <!-- Empty State -->
          <div v-else class="text-center pa-6 grey--text">
            No data to preview. Please upload a file.
          </div>
        </div>
      </template>
  
      <!-- Dialog Actions -->
      <template v-slot:actions v-if="showActions">
        <v-spacer></v-spacer>
        <v-btn 
          @click="handleSubmit" 
          color="primary"
          :disabled="submitDisabled"
        >
          <span>Submit</span>
        </v-btn>
      </template>
    </lb-dialog>
  </template>
  
  <script>
  /**
   * UploadRevenueCommentaryDialog Component
   * 
   * A dialog for uploading and previewing revenue breakup commentary data
   * from Excel files before submission.
   * 
   * Features:
   * - File upload with validation
   * - Data preview with formatted numbers
   * - Template download
   * - Error display for invalid rows
   * 
   * @component
   * @example
   * <UploadRevenueCommentaryDialog
   *   v-model="uploadrevenuecommentarydialog"
   *   :loading="uploadrevenuecommentaryloading"
   *   :upload-data="uploadfiledata_revenue"
   *   :headers="clientbyclient_headers"
   *   :has-error="uploadclientbyclienterror"
   *   @file-change="processClientbyclientUploadFile('revenue')"
   *   @download-template="downloadRevenueCommentaryTemplate('revenue')"
   *   @submit="uploadclientbyclientCommentary('revenue')"
   * />
   */
  export default {
    name: 'UploadRevenueCommentaryDialog',
  
    props: {
      /**
       * Controls dialog visibility (v-model)
       * @type {Boolean}
       */
      value: {
        type: Boolean,
        default: false
      },
  
      /**
       * Loading state for the dialog
       * @type {Boolean}
       */
      loading: {
        type: Boolean,
        default: false
      },
  
      /**
       * Upload file data to preview
       * Array of objects containing revenue commentary data
       * @type {Array}
       */
      uploadData: {
        type: Array,
        default: () => []
      },
  
      /**
       * Table headers configuration
       * Should include client code, name, amounts, variances
       * @type {Array}
       */
      headers: {
        type: Array,
        default: () => []
      },
  
      /**
       * Flag indicating if there's an error in upload data
       * Hides submit button when true
       * @type {Boolean}
       */
      hasError: {
        type: Boolean,
        default: false
      }
    },
  
    data() {
      return {
        /**
         * Internal value for v-model support
         */
        internalValue: this.value,
  
        /**
         * Local file reference for upload
         */
        uploadFile: null,
  
        /**
         * Enabled slots for the data table
         * These slots handle custom rendering of specific columns
         */
        enabledSlots: [
          'actual_amount',
          'forecast_amount',
          'proforma_amount',
          'actual_vs_proforma_variance_amount',
          'actual_vs_forecast_variance_amount',
          'message'
        ]
      };
    },
  
    computed: {
      /**
       * Computed headers that includes message column
       * Adds validation message column to the provided headers
       * @returns {Array} Complete headers array
       */
      computedHeaders() {
        return [
          ...this.headers,
          { 
            text: 'Message', 
            value: 'message', 
            lock: true 
          }
        ];
      },
  
      /**
       * Determine if actions section should be shown
       * Shows only when there's data and no errors
       * @returns {Boolean}
       */
      showActions() {
        return !this.hasError && this.uploadData.length > 0;
      },
  
      /**
       * Determine if submit button should be disabled
       * @returns {Boolean}
       */
      submitDisabled() {
        return this.hasError || this.uploadData.length === 0 || this.loading;
      }
    },
  
    watch: {
      /**
       * Watch for external value changes
       * Updates internal state when parent changes value
       */
      value(newVal) {
        this.internalValue = newVal;
      },
  
      /**
       * Watch for internal value changes
       * Emits input event to update parent v-model
       */
      internalValue(newVal) {
        this.$emit('input', newVal);
        
        // Reset file when dialog closes
        if (!newVal) {
          this.uploadFile = null;
        }
      }
    },
  
    methods: {
      /**
       * Handle file change event
       * Emits file-change event with the selected file
       * @emits file-change
       */
      handleFileChange() {
        this.$emit('file-change', this.uploadFile);
      },
  
      /**
       * Handle download template button click
       * Emits download-template event
       * @emits download-template
       */
      handleDownloadTemplate() {
        this.$emit('download-template');
      },
  
      /**
       * Handle submit button click
       * Emits submit event to process upload
       * @emits submit
       */
      handleSubmit() {
        this.$emit('submit');
      },
  
      /**
       * Format number for display
       * Uses $nova formatter if available, otherwise fallback
       * @param {Number} value - The number to format
       * @returns {String} Formatted number string
       */
      formatNumber(value) {
        if (this.$nova && this.$nova.formatNumber) {
          return this.$nova.formatNumber(value);
        }
        
        // Fallback formatting
        if (value === null || value === undefined) return '-';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      }
    }
  };
  </script>
  
  <style scoped>
  /**
   * Styles for upload revenue commentary dialog
   */
  
  /* Gap utility for flex layouts */
  .gp-1 {
    display: flex;
    gap: 1%;
    justify-content: center;
  }
  
  /* Error text color */
  .error--text {
    color: #ff5252 !important;
  }
  
  /* Success text color */
  .success--text {
    color: #4caf50 !important;
  }
  
  /* Empty state styling */
  .text-center {
    text-align: center;
  }
  
  /* Grey text for labels and empty states */
  .grey--text {
    color: #9e9e9e !important;
  }
  
  /* Padding utilities */
  .pa-6 {
    padding: 24px;
  }
  
  .mb-2 {
    margin-bottom: 8px;
  }
  </style>